import { CurrencyPipe, formatCurrency } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostBinding,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CarrierModelKeys } from 'ag-common-lib/public-api';
import { CarrierService } from '../../services/carrier.service';
import { removeDiacritics } from '../../../shared/utils/remove-diacritics';

type CarrierTickerItem = {
  name: string;
  premiumTotal: number;
  share: number;
  color: string;
  imageUrl: string | null;
};

@Component({
  selector: 'ag-carrier-ticker',
  templateUrl: './carrier-ticker.component.html',
  styleUrls: ['./carrier-ticker.component.scss'],
  providers: [CurrencyPipe],
})
export class CarrierTickerComponent implements AfterViewInit, OnDestroy {
  private static readonly TICKER_ANIMATION_DURATION_MS = 54000;

  @HostBinding('class') className = 'carrier-ticker';

  @ViewChild('tickerContainerRef', { static: false }) tickerContainerRef?: ElementRef<HTMLDivElement>;
  @ViewChild('tickerTrackRef', { static: false }) tickerTrackRef?: ElementRef<HTMLDivElement>;
  @ViewChild('tickerSegmentRef', { static: false }) tickerSegmentRef?: ElementRef<HTMLDivElement>;

  readonly salesByCarrier = input<any[] | null>(null);
  readonly totalWeightedPremium = input<number | null>(null);

  private readonly carriersSignal = toSignal(this.carrierService.getList(), {
    initialValue: [] as any[],
  });

  protected readonly tickerItems = computed<CarrierTickerItem[]>(() => {
    const salesByCarrier = this.salesByCarrier() ?? [];
    const carriers = this.carriersSignal();

    if (!salesByCarrier.length) {
      return [];
    }

    const carriersNormalizedMap = new Map();
    const carriersMap = new Map();

    carriers?.forEach(carrier => {
      const carrierName = carrier?.[CarrierModelKeys.carrierName];
      if (!carrierName) {
        return;
      }

      carriersMap.set(carrierName, carrier);
      carriersNormalizedMap.set(removeDiacritics(carrierName.toLowerCase()), carrier);
    });

    const salesTotal = salesByCarrier.reduce((total, carrier) => total + (Number(carrier?.premiumTotal) || 0), 0);
    const weightedPremiumTotal = Number(this.totalWeightedPremium()) || salesTotal;

    return salesByCarrier
      .map(carrier => {
        const carrierNameKey = carrier?.name;
        const normalizedCarrierName = removeDiacritics(String(carrierNameKey ?? '').toLowerCase());
        const carrierInfo =
          carriersNormalizedMap.get(carrierNameKey) ??
          carriersNormalizedMap.get(normalizedCarrierName) ??
          carriersMap.get(carrierNameKey);
        const premiumTotal = Number(carrier?.premiumTotal) || 0;
        const share = weightedPremiumTotal > 0 ? premiumTotal / weightedPremiumTotal : 0;

        return {
          name: carrierInfo?.[CarrierModelKeys.carrierName] ?? carrierNameKey?.toUpperCase() ?? '',
          premiumTotal,
          share,
          color: carrierInfo?.carrier_color ?? '#17324d',
          imageUrl: carrierInfo?.[CarrierModelKeys.carrierImage] || null,
        };
      })
      .filter(carrier => carrier.premiumTotal > 0)
      .sort((left, right) => right.premiumTotal - left.premiumTotal);
  });

  protected readonly shouldLoopTicker = signal(false);
  protected readonly isTickerInteracting = signal(false);
  protected readonly tickerAnimationDelayMs = signal(0);

  private isSyncingTickerScroll = false;
  private tickerResizeObserver?: ResizeObserver;
  private tickerMeasureFrame?: number;
  private readonly brokenCarrierImages = signal(new Set<string>());

  constructor(
    private currencyPipe: CurrencyPipe,
    private carrierService: CarrierService,
  ) {
    effect(() => {
      this.tickerItems();
      this.scheduleTickerMeasurement();
    });
  }

  ngAfterViewInit(): void {
    this.startTickerResizeObserver();
    this.scheduleTickerMeasurement();
  }

  ngOnDestroy(): void {
    this.tickerResizeObserver?.disconnect();

    if (this.tickerMeasureFrame) {
      cancelAnimationFrame(this.tickerMeasureFrame);
    }
  }

  protected formatPremiumTotal = (value: number) => formatCurrency(value, 'en', '$', 'USD', '1.0-0');

  protected isBrokenCarrierImage = (imageUrl: string | null | undefined) => {
    if (!imageUrl) {
      return false;
    }

    return this.brokenCarrierImages().has(imageUrl);
  };

  protected handleCarrierImageError = (imageUrl: string | null | undefined) => {
    if (!imageUrl || this.brokenCarrierImages().has(imageUrl)) {
      return;
    }

    this.brokenCarrierImages.update(images => new Set(images).add(imageUrl));
  };

  protected handleTickerScroll = () => {
    if (this.isSyncingTickerScroll || !this.shouldLoopTicker()) {
      return;
    }

    const tickerContainer = this.tickerContainerRef?.nativeElement;
    const tickerSegment = this.tickerSegmentRef?.nativeElement;
    if (!tickerContainer) {
      return;
    }

    const loopWidth = tickerSegment?.scrollWidth ?? 0;
    if (!loopWidth) {
      return;
    }

    const maxScrollableLeft = tickerContainer.scrollWidth - tickerContainer.clientWidth;
    const scrollLeft = tickerContainer.scrollLeft;
    const edgeBuffer = 4;

    this.isSyncingTickerScroll = true;

    if (scrollLeft >= maxScrollableLeft - edgeBuffer) {
      tickerContainer.scrollLeft = scrollLeft - loopWidth;
    } else if (scrollLeft > 0 && scrollLeft <= edgeBuffer) {
      tickerContainer.scrollLeft = scrollLeft + loopWidth;
    }

    this.isSyncingTickerScroll = false;
  };

  protected pauseTicker = () => {
    this.syncAnimationOffsetToScroll();
    this.isTickerInteracting.set(true);
  };

  protected resumeTicker = () => {
    if (!this.shouldLoopTicker()) {
      return;
    }

    const tickerContainer = this.tickerContainerRef?.nativeElement;
    const tickerSegment = this.tickerSegmentRef?.nativeElement;
    if (!tickerContainer || !tickerSegment?.scrollWidth) {
      this.isTickerInteracting.set(false);
      return;
    }

    const segmentWidth = tickerSegment.scrollWidth;
    const normalizedScrollLeft = this.normalizeLoopOffset(tickerContainer.scrollLeft, segmentWidth);
    const progress = normalizedScrollLeft / segmentWidth;

    this.tickerAnimationDelayMs.set(-Math.round(progress * CarrierTickerComponent.TICKER_ANIMATION_DURATION_MS));

    this.isSyncingTickerScroll = true;
    tickerContainer.scrollLeft = 0;
    this.isSyncingTickerScroll = false;

    this.isTickerInteracting.set(false);
  };

  private startTickerResizeObserver = () => {
    const tickerContainer = this.tickerContainerRef?.nativeElement;
    const tickerSegment = this.tickerSegmentRef?.nativeElement;

    if (!tickerContainer || !tickerSegment || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.tickerResizeObserver?.disconnect();
    this.tickerResizeObserver = new ResizeObserver(() => {
      this.scheduleTickerMeasurement();
    });
    this.tickerResizeObserver.observe(tickerContainer);
    this.tickerResizeObserver.observe(tickerSegment);
  };

  private scheduleTickerMeasurement = () => {
    if (typeof requestAnimationFrame === 'undefined') {
      this.updateTickerLoopState();
      return;
    }

    if (this.tickerMeasureFrame) {
      cancelAnimationFrame(this.tickerMeasureFrame);
    }

    this.tickerMeasureFrame = requestAnimationFrame(() => {
      this.tickerMeasureFrame = undefined;
      this.startTickerResizeObserver();
      this.updateTickerLoopState();
    });
  };

  private updateTickerLoopState = () => {
    const tickerContainer = this.tickerContainerRef?.nativeElement;
    const tickerSegment = this.tickerSegmentRef?.nativeElement;
    const shouldLoop =
      !!tickerContainer &&
      !!tickerSegment &&
      this.tickerItems().length > 1 &&
      tickerSegment.scrollWidth > tickerContainer.clientWidth + 1;

    this.shouldLoopTicker.set(shouldLoop);

    if (!tickerContainer || !tickerSegment) {
      return;
    }

    if (!shouldLoop) {
      this.isTickerInteracting.set(false);
      this.tickerAnimationDelayMs.set(0);
      tickerContainer.scrollLeft = 0;
      return;
    }

    const segmentWidth = tickerSegment.scrollWidth;
    if (segmentWidth && tickerContainer.scrollLeft >= segmentWidth) {
      tickerContainer.scrollLeft -= segmentWidth;
    }
  };

  private syncAnimationOffsetToScroll = () => {
    const tickerContainer = this.tickerContainerRef?.nativeElement;
    const tickerTrack = this.tickerTrackRef?.nativeElement;
    const tickerSegment = this.tickerSegmentRef?.nativeElement;

    if (!this.shouldLoopTicker() || !tickerContainer || !tickerTrack || !tickerSegment?.scrollWidth) {
      return;
    }

    const segmentWidth = tickerSegment.scrollWidth;

    const translateX = this.getTranslateX(tickerTrack);
    const effectiveScrollLeft = tickerContainer.scrollLeft - translateX;
    const normalizedScrollLeft = this.normalizeLoopOffset(effectiveScrollLeft, segmentWidth);

    this.tickerAnimationDelayMs.set(
      -Math.round((normalizedScrollLeft / segmentWidth) * CarrierTickerComponent.TICKER_ANIMATION_DURATION_MS),
    );

    this.isSyncingTickerScroll = true;
    tickerContainer.scrollLeft = normalizedScrollLeft;
    this.isSyncingTickerScroll = false;
  };

  private getTranslateX = (element: HTMLDivElement) => {
    const transform = getComputedStyle(element).transform;

    if (!transform || transform === 'none') {
      return 0;
    }

    if (transform.startsWith('matrix3d(')) {
      const values = transform
        .slice(9, -1)
        .split(',')
        .map(value => Number(value.trim()));
      return values[12] || 0;
    }

    if (transform.startsWith('matrix(')) {
      const values = transform
        .slice(7, -1)
        .split(',')
        .map(value => Number(value.trim()));
      return values[4] || 0;
    }

    return 0;
  };

  private normalizeLoopOffset = (offset: number, segmentWidth: number) => {
    if (!segmentWidth) {
      return 0;
    }

    return ((offset % segmentWidth) + segmentWidth) % segmentWidth;
  };
}
